import { useMemo, useState } from 'react';
import { BUSINESS_TZ, type StablePoint } from '@wave/shared';
import { EChart, type EChartsOption } from '../EChart';
import { CHART_COLORS, tooltipFit, valueAxisLabel } from './ChartsPanel';
import { useIsNarrow } from '../../lib/useMediaQuery';
import { DivergenceModal } from './DivergenceModal';
import { useDayReconciliation } from './useBalanceHistory';
import { shiftDate, todayIn } from './dailyMath';
import { groupTopItems } from './robustMath';

const fmt = (v: number) => v.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtSigned = (v: number) => `${v > 0 ? '+' : ''}${fmt(v)}`;
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', { timeZone: BUSINESS_TZ, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const fmtDate = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}.${m}`;
};

const TOP_N = 10;
const REVAL_KEY = '__reval';

function PointBox({ label, point }: { label: string; point: StablePoint | null }) {
  return (
    <div className="rounded-md border border-border bg-bg px-3 py-2">
      <div className="text-xs text-text2">{label}</div>
      {point ? (
        <>
          <div className="text-lg font-semibold tabular-nums text-text">{fmt(Number(point.positionUsdt))}</div>
          <div className="text-xs text-text2">
            {fmtTime(point.ts)} МСК
            {!point.stable && <span className="ml-1 text-warning" title="В окне 2 ч перед границей не нашлось устойчивого снапшота — шла операция «в пути».">⚠ неустойчиво</span>}
          </div>
        </>
      ) : (
        <div className="text-sm text-text2">нет снапшотов</div>
      )}
    </div>
  );
}

/**
 * Сверка суток: позиция на начало → на конец (устойчивые точки) и из чего
 * сложилась разница — вклад каждого чата/кошелька по курсу конца суток плюс
 * переоценка. Отвечает на «вчера было X, сегодня Y — почему?» без ручного
 * разбора снапшотов. Непарные движения журнала — кандидаты в «кто-то не
 * провёл вторую ногу».
 */
export function DayReconPanel({
  date,
  onDateChange,
  onOpenChat,
}: {
  /** null — текущие сутки. */
  date: string | null;
  onDateChange: (date: string | null) => void;
  onOpenChat: (chatId: string, title: string) => void;
}) {
  const today = todayIn(BUSINESS_TZ);
  const current = date ?? today;
  const { data, isLoading } = useDayReconciliation(date);
  const narrow = useIsNarrow();
  const [unmatchedOpen, setUnmatchedOpen] = useState(false);

  const bars = useMemo(() => {
    if (!data) return [];
    const items = data.items.map((it) => ({ key: it.chatId, label: it.title, value: Number(it.deltaUsdt) }));
    const reval = Number(data.revaluationUsdt ?? 0);
    const withReval = Math.abs(reval) >= 1 ? [...items, { key: REVAL_KEY, label: 'Переоценка курсов', value: reval }] : items;
    // Снизу вверх по модулю: самый крупный вклад — наверху графика.
    return groupTopItems(withReval, TOP_N).reverse();
  }, [data]);

  const option = useMemo<EChartsOption>(
    () => ({
      animation: false,
      grid: { left: 8, right: 64, top: 8, bottom: 8, containLabel: true },
      tooltip: {
        ...tooltipFit(narrow),
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const i = (params as Array<{ dataIndex: number }>)[0]?.dataIndex ?? 0;
          const b = bars[i];
          return b ? `${b.label}<br/>вклад в позицию: <b>${fmtSigned(b.value)}</b> USDT` : '';
        },
      },
      xAxis: { type: 'value', axisLabel: valueAxisLabel(narrow), splitLine: { lineStyle: { color: CHART_COLORS.grid } } },
      yAxis: {
        type: 'category',
        // На телефоне подписи короче (полное имя — в тултипе), иначе съедают ширину баров.
        data: bars.map((b) => {
          const max = narrow ? 12 : 26;
          return b.label.length > max ? `${b.label.slice(0, max - 1)}…` : b.label;
        }),
        axisLabel: { color: CHART_COLORS.text, ...(narrow ? { fontSize: 11 } : {}) },
      },
      series: [
        {
          type: 'bar',
          data: bars.map((b) => ({
            value: b.value,
            itemStyle: { color: b.key === REVAL_KEY ? '#94a3b8' : b.value >= 0 ? CHART_COLORS.neg : CHART_COLORS.pos },
          })),
          label: { show: true, position: 'right', color: CHART_COLORS.text, formatter: (p: { value: number }) => fmtSigned(p.value) },
        },
      ],
    }),
    [bars, narrow],
  );

  const delta = data?.deltaUsdt === null || data?.deltaUsdt === undefined ? null : Number(data.deltaUsdt);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="h-8 rounded border border-border px-2 text-sm hover:bg-bg3" onClick={() => onDateChange(shiftDate(current, -1))}>
          ‹
        </button>
        <span className="min-w-28 text-center text-sm font-medium text-text">
          {current === today ? `Сегодня, ${fmtDate(current)}` : fmtDate(current)}
        </span>
        <button
          type="button"
          className="h-8 rounded border border-border px-2 text-sm hover:bg-bg3 disabled:opacity-40"
          disabled={current >= today}
          onClick={() => {
            const next = shiftDate(current, 1);
            onDateChange(next >= today ? null : next);
          }}
        >
          ›
        </button>
        {current !== today && (
          <button type="button" className="h-8 rounded px-2 text-xs text-accent hover:bg-bg3" onClick={() => onDateChange(null)}>
            к сегодня
          </button>
        )}
      </div>

      {isLoading || !data ? (
        <div className="mt-3 h-40 animate-pulse rounded bg-bg3" />
      ) : (
        <>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <PointBox label="Начало суток" point={data.from} />
            <PointBox label={current === today ? 'Сейчас' : 'Конец суток'} point={data.to} />
            <div className="rounded-md border border-border bg-bg px-3 py-2">
              <div className="text-xs text-text2">Изменение позиции</div>
              <div className={`text-lg font-semibold tabular-nums ${delta === null ? 'text-text2' : delta >= 0 ? 'text-success' : 'text-danger'}`}>
                {delta === null ? '—' : fmtSigned(delta)}
              </div>
              <div className="text-xs text-text2">
                = {data.items.length} вкладов чатов/кошельков
                {data.revaluationUsdt !== null && ` + переоценка ${fmtSigned(Number(data.revaluationUsdt))}`}
              </div>
            </div>
          </div>

          {data.unmatched.count > 0 && (
            <button
              type="button"
              onClick={() => setUnmatchedOpen(true)}
              className="mt-2 w-full rounded-md border border-[var(--yellow-border)] bg-[var(--yellow-bg)] px-3 py-2 text-left text-sm text-warning hover:opacity-90"
            >
              ⚠ Непарных движений от {fmt(data.unmatched.minUsdt)} USDT: <b>{data.unmatched.count}</b> на{' '}
              <b>{fmt(Number(data.unmatched.sumAbsUsdt))}</b> USDT
              {data.unmatched.truncated && ' (список неполный)'} — вероятно, не проведена вторая нога. Открыть →
            </button>
          )}

          {bars.length > 0 ? (
            <EChart option={option} height={Math.max(160, bars.length * 28 + 24)} />
          ) : (
            <div className="mt-3 text-sm text-text2">Остатки чатов и кошельков за сутки не менялись.</div>
          )}

          {data.items.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-text2">
                    <th className="py-1.5 pr-3 font-medium">Чат / кошелёк</th>
                    <th className="py-1.5 pr-3 font-medium">Изменение остатка</th>
                    <th className="py-1.5 text-right font-medium">Вклад, USDT</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr key={it.chatId} className="border-b border-border/60">
                      <td className="py-1.5 pr-3">
                        <button type="button" className="text-left text-accent hover:underline" onClick={() => onOpenChat(it.chatId, it.title)}>
                          {it.source === 'tron' ? '👛 ' : ''}
                          {it.title}
                        </button>
                      </td>
                      <td className="py-1.5 pr-3 tabular-nums text-text2">
                        {it.ccys.map((c) => `${c.ccy} ${fmtSigned(Number(c.deltaAmount))}`).join(' · ')}
                      </td>
                      <td className={`py-1.5 text-right tabular-nums ${Number(it.deltaUsdt) >= 0 ? 'text-success' : 'text-danger'}`}>
                        {fmtSigned(Number(it.deltaUsdt))}
                      </td>
                    </tr>
                  ))}
                  {data.revaluationUsdt !== null && Math.abs(Number(data.revaluationUsdt)) >= 1 && (
                    <tr className="border-b border-border/60 text-text2">
                      <td className="py-1.5 pr-3" colSpan={2} title="Смена курса при неизменной сумме + чаты без курса">
                        Переоценка курсов и прочее
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{fmtSigned(Number(data.revaluationUsdt))}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {unmatchedOpen && data && (
        <DivergenceModal
          from={data.from?.ts ?? `${current}T00:00:00Z`}
          to={data.to?.ts ?? new Date().toISOString()}
          onClose={() => setUnmatchedOpen(false)}
        />
      )}
    </div>
  );
}
